import styled from '@emotion/styled';
import React, { useState } from 'react';
import { Content, NavigationConfiguration } from './apiService';
import ContentGenres from './ContentGenres';
import ContentRatings from './ContentRatings';
import CountryYear from './CountryYear';
import { Card, CardBody, CardImage, Ellipsis, Link, Text } from './styles';
import CardBadge from './CardBadge';
import DesktopOnlyWrapper from './styles/DesktopOnlyWrapper';
import useDelayedHover from './useDelayedHover';
import { getNavigationUrl, getWidgetsTeaserPath } from './utils';
import PromotedCardVideoPreview from './PromotedCardVideoPreview';

interface PromotedCardProps {
  id?: string;
  imagePath?: string;
  content?: Content;
  navigationConfiguration?: NavigationConfiguration;
  title?: string;
  description?: string;
  active?: boolean;
  onHoverChange?: (id: string | null) => void;
}

export default function PromotedCard({
  id,
  imagePath,
  content,
  navigationConfiguration,
  title: itemTitle,
  description,
  active = false,
  onHoverChange,
}: PromotedCardProps) {
  const { short_id, page_title: pageTitle, slug, flag } = content ?? {};
  const cardId = id ?? content?.id;
  const isNonContentNavigation = navigationConfiguration?.navigation_type !== 'content';
  const title = isNonContentNavigation ? itemTitle : pageTitle;
  const displayDescription = isNonContentNavigation ? description : undefined;
  const teaserPath = getWidgetsTeaserPath(content?.video_files, content?.id);
  const href =
    (navigationConfiguration ? getNavigationUrl(navigationConfiguration, slug, short_id) : `/contents/${short_id}/${slug}`) ?? '#';

  const [hovered, setHovered] = useState(false);

  const delayedHover = useDelayedHover((isHovered) => {
    setHovered(isHovered);
    onHoverChange?.(teaserPath && isHovered ? cardId ?? null : null);
  });

  const onMouseEnter = () => {
    delayedHover(true);
  };

  const onMouseLeave = () => {
    delayedHover(false);
  };

  return (
    <Card orientation="horizontal" style={{ backgroundImage: 'url(/static/images/placeholder/promoted.svg)' }} as={Link} href={href} block>
      <CardImage src={`${imagePath}?w=500`} alt={title} loading="lazy" />
      {!isNonContentNavigation && !(hovered && teaserPath?.src) && <CardBadge flag={flag ?? ''} />}
      <MobileCard>
        <CardBody>
          <DetailInfo content={content} title={title} description={displayDescription} isNonContentNavigation={isNonContentNavigation} />
        </CardBody>
      </MobileCard>
      <DesktopOnlyWrapper>
        <CardHoverBody hasTeaser={!!teaserPath} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          {hovered && (
            <>
              {teaserPath?.src ? (
                <PromotedCardVideoPreview
                  teaser={teaserPath}
                  imagePath={imagePath}
                  active={active}
                  content={content}
                  title={title}
                  description={displayDescription}
                  isNonContentNavigation={isNonContentNavigation}
                />
              ) : (
                <DetailInfo
                  content={content}
                  title={title}
                  description={displayDescription}
                  isNonContentNavigation={isNonContentNavigation}
                />
              )}
            </>
          )}
        </CardHoverBody>
      </DesktopOnlyWrapper>
    </Card>
  );
}

const DetailInfo = ({ content, title, description, isNonContentNavigation }) => {
  return (
    <Container>
      {title && (
        <Title mb={1} variant="body1" as="h2">
          {title}
        </Title>
      )}
      {isNonContentNavigation ? (
        description && (
          <Text variant="body3" as="div" mb={1} color="weak">
            <Ellipsis>{description}</Ellipsis>
          </Text>
        )
      ) : (
        <>
          {description ? (
            <Text variant="body3" as="div" mb={1} color="weak">
              <Ellipsis>{description}</Ellipsis>
            </Text>
          ) : (
            <div className="desktop-only">
              <CountryYear mb={1} label {...content} />
              <ContentRatings styleSystem={{ mb: 1 }} {...content} />
            </div>
          )}
          <ContentGenres categories={content.categories} link={false} nowrap />
        </>
      )}
    </Container>
  );
};

const MobileCard = styled.div(() => ({
  display: 'block',
  '@media (min-width: 1200px)': {
    display: 'none',
  },
}));

const CardHoverBody = styled(CardBody)<{ hasTeaser: boolean }>(({ theme, hasTeaser }) => {
  return {
    zIndex: 1,
    padding: hasTeaser ? 0 : theme.spacing(1),
    ...(hasTeaser && {
      backgroundColor: 'transparent',
    }),
  };
});

const Container = styled.div(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '& .desktop-only': {
    display: 'none',
  },
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 2),
    '& .desktop-only': {
      display: 'block',
    },
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2, 4),
  },
}));

const Title = styled(Text)(({ theme }) => ({
  fontWeight: theme.font.weight.demibold,
  ...theme.font.responsiveSize(1),
  [theme.breakpoints.up('md')]: {
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flexShrink: 0,
  },
}));
